import * as Sentry from '@sentry/browser';

const DSN = import.meta.env.VITE_GLITCHTIP_DSN;

export function initSentry() {
  if (!DSN) {
    console.warn('[Sentry] VITE_GLITCHTIP_DSN not configured - error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: DSN,
    tunnel: '/api/sentry-tunnel',
    environment: import.meta.env.MODE, // 'development' or 'production'
    release: import.meta.env.VITE_APP_VERSION || '0.1.0',

    // Only send errors in production by default
    enabled: import.meta.env.PROD,

    // Sample rate for error events (1.0 = 100%)
    sampleRate: 1.0,

    // Don't send PII by default
    sendDefaultPii: false,

    // Filter out common noise
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      // Network errors that aren't actionable
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // User-initiated navigation
      'AbortError',
      'ResizeObserver loop',
    ],

    beforeSend(event, _hint) {
      // Don't send events in development unless explicitly enabled
      if (import.meta.env.DEV && !import.meta.env.VITE_GLITCHTIP_DEV_ENABLED) {
        console.log('[Sentry] Would send event (dev mode):', event);
        return null;
      }

      // Scrub sensitive data from URLs
      if (event.request?.url) {
        event.request.url = event.request.url.replace(/token=[^&]+/g, 'token=REDACTED');
      }

      return event;
    },
  });

  console.log('[Sentry] Initialized for', import.meta.env.MODE);
}

// Export Sentry for manual error capture
export { Sentry };

// Helper to capture errors with additional context
export function captureError(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.setContext('additional', context);
  }
  Sentry.captureException(error);
}

// Helper to set user context (call after authentication)
export function setUserContext(user: { id: string; email?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  });
}

// Helper to clear user context (call on logout)
export function clearUserContext() {
  Sentry.setUser(null);
}
